import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { getOne, postJson } from '@/shared/api/http';

import { isVerificationStatus } from '@/shared/api/generated/audit';
import type { VerificationStatus } from '@/shared/api/generated/audit';

export const VERIFY_CHAIN_URL = '/v1/audit/verify-chain';

/* No X-Lumiq-Tenant header, here or anywhere in this client. Hard rule 1: only the edge sets it,
 * from a validated token claim. That matters more here than on most endpoints — the *tenant* is what
 * scopes which verification result this reads, so a browser-supplied one would let a caller poll
 * another tenant's. The backend keys the lookup on the claim and the chain date for exactly that
 * reason. */

/* The poll interval while a verification is running. Chain verification re-hashes a whole day, which
 * takes seconds on a small tenant-day and minutes on a large one, so this polls rather than assuming.
 * Stops the moment the result reports `finished` — see `refetchInterval` below. */
const POLL_MS = 3_000;

function yesterdayUtc(): string {
  /* Yesterday, matching the server's default. Today's chain is still being appended to, so verifying
   * it reads a partial chain and reports a divergence that resolves itself — the fastest way to teach
   * an operator to ignore this alarm. */
  const now = new Date();
  const yesterday = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1),
  );
  return yesterday.toISOString().slice(0, 10);
}

export const DEFAULT_CHAIN_DATE = yesterdayUtc;

async function fetchVerification(chainDate: string): Promise<VerificationStatus> {
  return getOne(
    `${VERIFY_CHAIN_URL}/${chainDate}`,
    isVerificationStatus,
    'verification status',
  );
}

export function useChainVerification(
  chainDate: string,
): UseQueryResult<VerificationStatus> {
  return useQuery({
    queryKey: ['audit', 'verify-chain', chainDate],
    queryFn: () => fetchVerification(chainDate),
    /* Polls only while the job is unfinished. A fixed interval would keep asking after a verdict is
     * in, and a verdict does not change — the chain for a past day is immutable. */
    refetchInterval: (query) => (query.state.data?.finished === true ? false : POLL_MS),
    retry: false,
  });
}

export function useVerifyChain(chainDate: string): UseMutationResult<void, Error, void> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      /* 503 is the expected answer on a deployment with no Celery broker, and it is not a bug —
       * `/readiness` reports CHAIN_VERIFICATION as a stub. The shared transport surfaces it as the
       * server's own RFC 9457 detail, because "nothing happened and here is why" is the useful
       * message. The key is generated per click rather than per session: two deliberate clicks are
       * two requests, and the *work* is deduplicated server-side on the tenant and the date anyway. */
      await postJson(VERIFY_CHAIN_URL, {
        idempotencyKey: `verify-chain-${chainDate}-${String(Date.now())}`,
        body: { chain_date: chainDate },
      });
    },
    onSuccess: () => {
      /* Invalidate rather than write an optimistic result. There is no result yet — the POST returns
       * 202 — and an optimistic "verified" would be the exact lie this whole subsystem exists to
       * prevent. */
      void client.invalidateQueries({ queryKey: ['audit', 'verify-chain', chainDate] });
    },
  });
}
